import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { useTranslation } from 'react-i18next'
import { useWindowDimensions } from '../../services/hooks/useWindowDimensions'
import { Button, TextField, Typography, Tab, Tabs, Box, Table, TableCell, TableRow, TableBody } from '@mui/material'
import PropTypes from 'prop-types'
import theme from './theme'

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ paddingInline: 2, paddingTop: 2 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  tab: PropTypes.number.isRequired,
}

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

const COLOR_FULL = 3;
const COLOR_BLACK = 1;
const COLLATE_FALSE = 1;
const COLLATE_TRUE = 2;
const OFFSET_FALSE = 1;
const OFFSET_TRUE = 2;
const SIDES_SINGLE = 1;
const SIDES_DOUBLE = 2;
const SIDES_BOOKLET = 3;
const BINDING_LEFT = 1;
const BINDING_RIGHT = 2;
const BINDING_TOP = 3;
const STAPLE_NONE = 1;
const STAPLE_CORNER = 2;
const STAPLE_TWO = 3;
const FOLD_NONE = 1;
const FOLD_HALF = 2;
const PUNCH_NONE = 1;
const PUNCH_TWO = 2;
const PUNCH_THREE = 3;

export function PrintSettings(props) {
  const { userPreferences, setUserPreferences, selected, optimizedJobs } = props
  const { width } = useWindowDimensions()

  const [tab, setTab] = useState(0);
  const [copies, setCopies] = useState(1)
  // Basic
  const [color, setColor] = useState(COLOR_BLACK)
  const [collate, setCollate] = useState(COLLATE_TRUE)
  const [offset, setOffset] = useState(OFFSET_FALSE)
  // Layout
  const [sides, setSides] = useState(SIDES_SINGLE)
  const [binding, setBinding] = useState(BINDING_LEFT)
  //Finisher
  const [staple, setStaple] = useState(STAPLE_NONE)
  const [fold, setFold] = useState(FOLD_NONE)
  const [punch, setPunch] = useState(PUNCH_NONE)

  const { t } = useTranslation()

  const useStyles = makeStyles({
    card: {
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        height: '72vh',
        position: 'absolute',
        top: width <= 800 ? '26.3%' : '23.6%',
        left: '0.0%',
        zIndex: '5',
    }
  })
  const classes = useStyles()

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  }

  const handleCopiesChange = (event) => {
    const value = Number(event.target.value.replace(/[^0-9]+/g, ''))
    const userPref = userPreferences
    if (value >= 0 && value <= 9999) {
      setCopies(value)
      setUserPreferences({
        ...userPref,
        copies: value
      })
    }  
  }

  const jobCountsByFeature = Object.values(selected).reduce(
    (acc, currJob) => {
      if (currJob?.isUnivPrintJob) {
        acc.r2mJobs += 1
      } else {
        acc.univPrintJobs += 1
      }
      return acc
    },
    { r2mJobs: 0, univPrintJobs: 0 }
  )

  useEffect(() => {
    const selectedJobs = Object.values(selected).filter(job => !job?.isUnivPrintJob)
    const pjlHeadersMap = new Map()
    selectedJobs.forEach((job) => {
      const currentJob = optimizedJobs.filter(item => item.id === job.id)
      const p = currentJob[0].preferences
      if (!p.binding_editable) setUserPreferences((r) => { return { ...r, binding_editable: false }})
      if (!p.collate_editable) setUserPreferences((r) => { return { ...r, collate_editable: false }})
      if (!p.color_editable) setUserPreferences((r) => { return { ...r, color_editable: false }}) 
      if (!p.fold_editable) setUserPreferences((r) => { return { ...r, fold_editable: false }}) 
      if (!p.layout_editable) setUserPreferences((r) => { return { ...r, layout_editable: false }}) 
      if (!p.offset_editable) setUserPreferences((r) => { return { ...r, offset_editable: false }}) 
      if (!p.punch_editable) setUserPreferences((r) => { return { ...r, punch_editable: false }}) 
      if (!p.staple_editable) setUserPreferences((r) => { return { ...r, staple_editable: false }})
      for (const header of p.headers) {
        if ((header.active ?? true) !== true) {
          continue
        }
        if (!pjlHeadersMap.has([header.name, header.value])) {
          pjlHeadersMap.set([header.name, header.value].join(), {...header, enabled: header.defaultEnabled ?? true})
        } else if (header.defaultEnabled === false) {
          pjlHeadersMap.set([header.name, header.value].join(), {...header, enabled: header.defaultEnabled})
        }
      }
    })
    if (pjlHeadersMap.size > 0) setUserPreferences((r) => { return { ...r, pjlHeadersMap }})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={`${classes.card}`}>
      <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex', height: '100%' }}>
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={tab}
          onChange={handleTabChange}
          aria-label="Vertical tabs example"
          sx={{ borderRight: 1, borderColor: 'divider', bgcolor: '#D5CDE5', width: 170}}
        >
          <Tab 
            label={t("Basic")}
            sx={{
              color: tab === 0 ? 'white !important' : 'black !important',
              bgcolor: tab === 0 ? '#5F3FA5' : 'inherit',
              fontWeight: 'bold',
              fontSize: 18,
              textTransform: 'none',
              alignItems: 'start',
            }}
            {...a11yProps(0)}/>
          <Tab 
            label={t("Layout")}
            sx={{ 
              color: tab === 1 ? 'white !important' : 'black !important',
              bgcolor: tab === 1 ? '#5F3FA5' : 'inherit',
              fontWeight: 'bold',
              fontSize: 18,
              textTransform: 'none',
              alignItems: 'start'
            }}
            {...a11yProps(1)} />
          <Tab 
            label={t("Finisher")}
            sx={{ 
              color: tab === 2 ? 'white !important' : 'black !important',
              bgcolor: tab === 2 ? '#5F3FA5' : 'inherit',
              fontWeight: 'bold',
              fontSize: 18,
              textTransform: 'none',
              alignItems: 'start',
            }}
            {...a11yProps(2)} />
          {userPreferences.pjlHeadersMap && userPreferences.pjlHeadersMap.size > 0 && <Tab
            label={t("Options")}
            sx={{ 
              color: tab === 3 ? 'white !important' : 'black !important',
              bgcolor: tab === 3 ? '#5F3FA5' : 'inherit',
              fontWeight: 'bold',
              fontSize: 18,
              textTransform: 'none',
              alignItems: 'start',
            }}
            {...a11yProps(3)} /> }
        </Tabs>

        <Box sx={{display: 'flex', height: '100%', flexDirection: 'column'}}>
          <TabPanel value={tab} index={0}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ display: 'flex', flexDirection: 'row', border: 'none', paddingX: 0}}>
                    <Typography sx={{ fontSize: 20, fontWeight: 'normal', color: 'red' }}>*</Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 'normal'}}>{t('Copies')}</Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}} colSpan={2}>
                    <TextField
                      size='small'
                      type='number'
                      sx={{ width: 290 }}
                      value={copies === 0 ? '' : copies.toString() || ''}
                      onChange={handleCopiesChange}
                      onKeyDown={(event) => {
                        if (event.key === '.' || event.key === '+' || event.key === '-') event.preventDefault()
                      }}
                      onBlur={() => {
                        if (copies === 0) {
                          const userPref = userPreferences
                          setCopies(1)
                          setUserPreferences({
                            ...userPref,
                            copies: 1
                          })
                        }
                      }}
                    ></TextField>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', paddingX: 0}}>
                    <Typography sx={{ fontSize: 20, fontWeight: 'normal' }}>{t('Color')}</Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setColor(COLOR_FULL)
                        setUserPreferences({
                          ...userPref,
                          color: '3'
                        })
                      }}
                      variant={ color === COLOR_FULL ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: color === COLOR_FULL ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: color === COLOR_FULL ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: color === COLOR_FULL ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: color === COLOR_FULL ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: color === COLOR_FULL ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: color === COLOR_FULL ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: color === COLOR_FULL ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: color === COLOR_FULL ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.color_editable}
                    >{t('Color')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setColor(COLOR_BLACK)
                        setUserPreferences({
                          ...userPref,
                          color: '1'
                        })
                      }}
                      variant={ color === COLOR_BLACK ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: color === COLOR_BLACK ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: color === COLOR_BLACK ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: color === COLOR_BLACK ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: color === COLOR_BLACK ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: color === COLOR_BLACK ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: color === COLOR_BLACK ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: color === COLOR_BLACK ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: color === COLOR_BLACK ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.color_editable}
                    >{t('Black')}</Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', paddingX: 0}}>
                    <Typography sx={{ fontSize: 20, fontWeight: 'normal'}}>{t('Collate')}</Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setCollate(COLLATE_FALSE)
                        setUserPreferences({
                          ...userPref,
                          collate: 'False'
                        })
                      }}
                      variant={ collate === COLLATE_FALSE ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: collate === COLLATE_FALSE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: collate === COLLATE_FALSE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: collate === COLLATE_FALSE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: collate === COLLATE_FALSE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: collate === COLLATE_FALSE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: collate === COLLATE_FALSE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: collate === COLLATE_FALSE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: collate === COLLATE_FALSE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.collate_editable}
                    >{t('Off')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setCollate(COLLATE_TRUE)
                        setUserPreferences({
                          ...userPref,
                          collate: 'True'
                        })
                      }}
                      variant={ collate === COLLATE_TRUE ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: collate === COLLATE_TRUE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: collate === COLLATE_TRUE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: collate === COLLATE_TRUE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: collate === COLLATE_TRUE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: collate === COLLATE_TRUE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: collate === COLLATE_TRUE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: collate === COLLATE_TRUE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: collate === COLLATE_TRUE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.collate_editable}
                    >{t('On')}</Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', paddingX: 0}}>
                    <Typography sx={{ fontSize: 20, fontWeight: 'normal'}}>{t('Offset')}</Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setOffset(OFFSET_FALSE)
                        setUserPreferences({
                          ...userPref,
                          offset: 'False'
                        })
                      }}
                      variant={ offset === OFFSET_FALSE ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: offset === OFFSET_FALSE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: offset === OFFSET_FALSE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: offset === OFFSET_FALSE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: offset === OFFSET_FALSE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: offset === OFFSET_FALSE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: offset === OFFSET_FALSE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: offset === OFFSET_FALSE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: offset === OFFSET_FALSE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.offset_editable}
                    >{t('Off')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setOffset(OFFSET_TRUE)
                        setUserPreferences({
                          ...userPref,
                          offset: 'True'
                        })
                      }}
                      variant={ offset === OFFSET_TRUE ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: offset === OFFSET_TRUE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: offset === OFFSET_TRUE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: offset === OFFSET_TRUE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: offset === OFFSET_TRUE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: offset === OFFSET_TRUE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: offset === OFFSET_TRUE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: offset === OFFSET_TRUE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: offset === OFFSET_TRUE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.offset_editable}
                    >{t('On')}</Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 'none', paddingX: 0}}>
                    <Typography sx={{ fontSize: 20, fontWeight: 'normal'}}>{t('Sides')}</Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setSides(SIDES_SINGLE)
                        setUserPreferences({
                          ...userPref,
                          layout: 'NORMAL'
                        })
                      }}
                      variant={ sides === SIDES_SINGLE ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: sides === SIDES_SINGLE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: sides === SIDES_SINGLE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: sides === SIDES_SINGLE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: sides === SIDES_SINGLE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: sides === SIDES_SINGLE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: sides === SIDES_SINGLE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: sides === SIDES_SINGLE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: sides === SIDES_SINGLE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.layout_editable}
                    >{t('1-Sided')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setSides(SIDES_DOUBLE)
                        setUserPreferences({
                          ...userPref,
                          layout: 'DUPLEX'
                        })
                      }}
                      variant={ sides === SIDES_DOUBLE ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: sides === SIDES_DOUBLE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: sides === SIDES_DOUBLE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: sides === SIDES_DOUBLE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: sides === SIDES_DOUBLE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: sides === SIDES_DOUBLE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: sides === SIDES_DOUBLE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: sides === SIDES_DOUBLE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: sides === SIDES_DOUBLE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.layout_editable}
                    >{t('2-Sided')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setSides(SIDES_BOOKLET)
                        setUserPreferences({
                          ...userPref,
                          layout: 'BOOKLET4IN1'
                        })
                      }}
                      variant={ sides === SIDES_BOOKLET ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: sides === SIDES_BOOKLET ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: sides === SIDES_BOOKLET ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: sides === SIDES_BOOKLET ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: sides === SIDES_BOOKLET ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: sides === SIDES_BOOKLET ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: sides === SIDES_BOOKLET ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: sides === SIDES_BOOKLET ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: sides === SIDES_BOOKLET ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.layout_editable}
                    >{t('Booklet')}</Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', paddingX: 0}}>
                    <Typography sx={{ fontSize: 20, fontWeight: 'normal'}}>{t('Binding')}</Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setBinding(BINDING_LEFT)
                        setUserPreferences({
                          ...userPref,
                          binding: 'LEFT'
                        })
                      }}
                      variant={ binding === BINDING_LEFT ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: binding === BINDING_LEFT ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: binding === BINDING_LEFT ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: binding === BINDING_LEFT ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: binding === BINDING_LEFT ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: binding === BINDING_LEFT ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: binding === BINDING_LEFT ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: binding === BINDING_LEFT ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: binding === BINDING_LEFT ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.binding_editable}
                    >{t('Left')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setBinding(BINDING_RIGHT)
                        setUserPreferences({
                          ...userPref,
                          binding: 'RIGHT'
                        })
                      }}
                      variant={ binding === BINDING_RIGHT ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: binding === BINDING_RIGHT ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: binding === BINDING_RIGHT ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: binding === BINDING_RIGHT ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: binding === BINDING_RIGHT ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: binding === BINDING_RIGHT ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: binding === BINDING_RIGHT ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: binding === BINDING_RIGHT ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: binding === BINDING_RIGHT ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.binding_editable}
                    >{t('Right')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setBinding(BINDING_TOP)
                        setUserPreferences({
                          ...userPref,
                          binding: 'TOP'
                        })
                      }}
                      variant={ binding === BINDING_TOP ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: binding === BINDING_TOP ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: binding === BINDING_TOP ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: binding === BINDING_TOP ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: binding === BINDING_TOP ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: binding === BINDING_TOP ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: binding === BINDING_TOP ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: binding === BINDING_TOP ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: binding === BINDING_TOP ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.binding_editable}
                    >{t('Top')}</Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 'none', paddingX: 0}}>
                    <Typography sx={{ fontSize: 20, fontWeight: 'normal'}}>{t('Staple')}</Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setStaple(STAPLE_NONE)
                        setUserPreferences({
                          ...userPref,
                          staple: ''
                        })
                      }}
                      variant={ staple === STAPLE_NONE ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: staple === STAPLE_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: staple === STAPLE_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: staple === STAPLE_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: staple === STAPLE_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: staple === STAPLE_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: staple === STAPLE_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: staple === STAPLE_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: staple === STAPLE_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.staple_editable}
                    >{t('None')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setStaple(STAPLE_CORNER)
                        setUserPreferences({
                          ...userPref,
                          staple: 'CORNER'
                        })
                      }}
                      variant={ staple === STAPLE_CORNER ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: staple === STAPLE_CORNER ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: staple === STAPLE_CORNER ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: staple === STAPLE_CORNER ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: staple === STAPLE_CORNER ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: staple === STAPLE_CORNER ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: staple === STAPLE_CORNER ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: staple === STAPLE_CORNER ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: staple === STAPLE_CORNER ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.staple_editable}
                    >{t('Corner')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setStaple(STAPLE_TWO)
                        setUserPreferences({
                          ...userPref,
                          staple: 'ANY2POINT'
                        })
                      }}
                      variant={ staple === STAPLE_TWO ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: staple === STAPLE_TWO ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: staple === STAPLE_TWO ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: staple === STAPLE_TWO ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: staple === STAPLE_TWO ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: staple === STAPLE_TWO ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: staple === STAPLE_TWO ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: staple === STAPLE_TWO ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: staple === STAPLE_TWO ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.staple_editable}
                    >{t('2 Position')}</Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', paddingX: 0,}}>
                    <Typography sx={{ fontSize: 20, fontWeight: 'normal'}}>{t('Fold')}</Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setFold(FOLD_NONE)
                        setUserPreferences({
                          ...userPref,
                          fold: ''
                        })
                      }}
                      variant={ fold === FOLD_NONE ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: fold === FOLD_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: fold === FOLD_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: fold === FOLD_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: fold === FOLD_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: fold === FOLD_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: fold === FOLD_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: fold === FOLD_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: fold === FOLD_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.fold_editable}
                    >{t('None')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}} colSpan={2}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setFold(FOLD_HALF)
                        setUserPreferences({
                          ...userPref,
                          fold: 'CENTERFOLDIN'
                        })
                      }}
                      variant={ fold === FOLD_HALF ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: fold === FOLD_HALF ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: fold === FOLD_HALF ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '160px',
                        '&:active': {
                          backgroundColor: fold === FOLD_HALF ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: fold === FOLD_HALF ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: fold === FOLD_HALF ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: fold === FOLD_HALF ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: fold === FOLD_HALF ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: fold === FOLD_HALF ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.fold_editable}
                    >{t('Half-Fold')}</Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', paddingX: 0}}>
                    <Typography sx={{ fontSize: 20, fontWeight: 'normal' }}>{t('Punch')}</Typography>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setPunch(PUNCH_NONE)
                        setUserPreferences({
                          ...userPref,
                          punch: ''
                        })
                      }}
                      variant={ punch === PUNCH_NONE ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: punch === PUNCH_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: punch === PUNCH_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: punch === PUNCH_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: punch === PUNCH_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: punch === PUNCH_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: punch === PUNCH_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: punch === PUNCH_NONE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: punch === PUNCH_NONE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.punch_editable}
                    >{t('None')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setPunch(PUNCH_TWO)
                        setUserPreferences({
                          ...userPref,
                          punch: '2'
                        })
                      }}
                      variant={ punch === PUNCH_TWO ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: punch === PUNCH_TWO ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: punch === PUNCH_TWO ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: punch === PUNCH_TWO ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: punch === PUNCH_TWO ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: punch === PUNCH_TWO ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: punch === PUNCH_TWO ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: punch === 2 ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: punch === 2 ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.punch_editable}
                    >{t('2 Hole')}</Button>
                  </TableCell>
                  <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                    <Button 
                      size='large'
                      onMouseUp={() => {
                        const userPref = userPreferences
                        setPunch(PUNCH_THREE)
                        setUserPreferences({
                          ...userPref,
                          punch: '3'
                        })
                      }}
                      variant={ punch === PUNCH_THREE ? 'contained' : 'outlined' }
                      sx={{
                        backgroundColor: punch === PUNCH_THREE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                        color: punch === PUNCH_THREE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        width: '140px',
                        '&:active': {
                          backgroundColor: punch === PUNCH_THREE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: punch === PUNCH_THREE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:focus': {
                          backgroundColor: punch === PUNCH_THREE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: punch === PUNCH_THREE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                        '&:hover': {
                          backgroundColor: punch === PUNCH_THREE ? theme.palette.primary.main : theme.palette.primary.contrastText,
                          color: punch === PUNCH_THREE ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        },
                      }}
                      disabled={!userPreferences.punch_editable}
                    >{t('3 Hole')}</Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TabPanel>

          <TabPanel value={tab} index={3}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 'none', paddingX: 0}}>
                    <Typography sx={{ fontSize: 18, fontWeight: 'normal'}}>{t('Enable the following custom options.')}</Typography>
                  </TableCell>
                </TableRow>
                {userPreferences.pjlHeadersMap && [...userPreferences.pjlHeadersMap?.values()].map((header) => {
                  return (
                    <TableRow key={header.name}>
                      <TableCell sx={{ border: 'none', paddingX: 0}}>
                        <Typography sx={{ fontSize: 20, fontWeight: 'normal'}}>{header.displayName ?? header.name}</Typography>
                      </TableCell>
                      <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                        <Button 
                          size='large'
                          onClick={() => {
                            const userPref = userPreferences
                            const prefMap = userPref.pjlHeadersMap
                            prefMap.set([header.name, header.value].join(), { ...header, enabled: false})
                            setUserPreferences({
                              ...userPref,
                              pjlHeadersMap: prefMap
                            })
                          }}
                          variant={ header.enabled === false ? 'contained' : 'outlined' }
                          sx={{ width: '140px' }}
                          disabled={!(header.selectable ?? true)}
                        >{t('Off')}</Button>
                      </TableCell>
                      <TableCell sx={{ border: 'none', paddingLeft: '10px', paddingRight: '0px'}}>
                        <Button 
                          size='large'
                          onClick={() => {
                            const userPref = userPreferences
                            const prefMap = userPref.pjlHeadersMap
                            prefMap.set([header.name, header.value].join(), { ...header, enabled: true})
                            setUserPreferences({
                              ...userPref,
                              pjlHeadersMap: prefMap
                            })
                          }}
                          variant={ header.enabled === true ? 'contained' : 'outlined' }
                          sx={{ width: '140px' }}
                          disabled={!(header.selectable ?? true)}
                        >{t('On')}</Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TabPanel>
          { jobCountsByFeature.r2mJobs > 0 && jobCountsByFeature.univPrintJobs > 0 && (
            <Box sx={{display: 'flex', marginTop: 'auto', paddingInline: 2, fontStyle: 'italic', fontWeight: 300}}>
              {t('Settings will not apply to files printed from Microsoft Universal Print')}
            </Box>
          )}
        </Box>
      </Box>
    </div>
  )
}